import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Textarea,
  Switch,
  FormHelperText,
  Grid,
} from '@chakra-ui/react'
import {
  createClientSchema,
  updateClientSchema,
  type CreateClientFormData,
  type UpdateClientFormData,
} from '@/lib/validations/auth'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'

interface ClientFormProps {
  clientId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

export function ClientForm({ clientId, onSuccess, onCancel }: ClientFormProps) {
  const colors = useThemeColors()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { showError, showSuccess } = useToastNotification()
  const isEditMode = !!clientId

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CreateClientFormData | UpdateClientFormData>({
    resolver: zodResolver(isEditMode ? updateClientSchema : createClientSchema),
      defaultValues: {
        code: '',
        name: '',
        database_name: '',
        status: true,
        config: null,
        description: '',
      },
  })

  const statusValue = watch('status')

  useEffect(() => {
    if (isEditMode && clientId) {
      const fetchClient = async () => {
        setIsLoading(true)
        try {
          const response = await apiClient.get(`/organization/clients/${clientId}`)
          const client = response.data?.data || response.data || {}

          reset({
            code: client.code || '',
            name: client.name || '',
            database_name: client.database_name || '',
            status: client.status !== undefined ? Boolean(client.status) : true,
            config: null,
            description: client.description || '',
          })
        } catch (error: any) {
          showError(error)
        } finally {
          setIsLoading(false)
        }
      }
      fetchClient()
    }
  }, [clientId, isEditMode, reset, showError])

  const onSubmit = async (data: CreateClientFormData | UpdateClientFormData) => {
    setIsSubmitting(true)
    try {
      const payload = {
        ...data,
        config: null, // Configuration removed from UI
      }

      if (isEditMode && clientId) {
        await apiClient.put(`/organization/clients/${clientId}`, payload)
        showSuccess('Success', 'Client updated successfully')
      } else {
        await apiClient.post('/organization/clients', payload)
        showSuccess('Success', 'Client created successfully')
      }

      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <Box p={4}>
        <Text>Loading client data...</Text>
      </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Client Code <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.code}>
            <Input
              {...register('code')}
              placeholder="e.g., JFC"
              textTransform="uppercase"
              isDisabled={isEditMode}
            />
            <FormHelperText>
              Uppercase letters, numbers, and underscores only. Cannot be changed after creation.
            </FormHelperText>
            <FormErrorMessage>{errors.code?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Client Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.name}>
            <Input {...register('name')} placeholder="e.g., Jollibee Foods Corporation" />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Database Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.database_name}>
            <Input
              {...register('database_name')}
              placeholder="e.g., sima_jfc"
              textTransform="lowercase"
              isDisabled={isEditMode}
            />
            <FormHelperText>
              Lowercase letters, numbers, and underscores only. Cannot be changed after creation.
            </FormHelperText>
            <FormErrorMessage>{errors.database_name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Status
          </FormLabel>
          <FormControl>
            <HStack>
              <Switch
                isChecked={statusValue}
                onChange={(e) => setValue('status', e.target.checked)}
                colorScheme="green"
              />
              <Text fontSize="sm" color={colors.text}>
                {statusValue ? 'Active' : 'Inactive'}
              </Text>
            </HStack>
            <FormHelperText>Active clients can be accessed by users</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Description
          </FormLabel>
          <FormControl isInvalid={!!errors.description}>
            <Textarea
              {...register('description')}
              placeholder="Optional description for this client"
              rows={4}
              maxLength={1000}
            />
            <FormHelperText>Maximum 1000 characters</FormHelperText>
            <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={isSubmitting} loadingText={isEditMode ? 'Saving...' : 'Creating...'}>
            {isEditMode ? 'Save' : 'Create Client'}
            {isSubmitting && (
              <Text as="span" ml={2} fontSize="sm">
                ...
              </Text>
            )}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
